import { AxiosInstance } from "axios";
import React from "react";
import { useDebounce } from "use-debounce";
import { httpRequest } from "../helpers/api";
import { showErrorMessage } from "../helpers/errorHandler";
import { generateQueryString } from "../helpers/generateQueryString";
import {
	IHttpResponse,
	INITIAL_PAGINATION,
	INITIAL_QUERY,
	IPagination,
	IPayloadPagination,
	IQuery,
} from "../helpers/pagination";

type Props<T> = {
	endpoint: string;
	initialQuery?: T;
	limit?: number;
	pushData?: boolean;
	apiRequest?: AxiosInstance;
	fetchable?: boolean;
	searchParamName?: string;
	isRefreshWhenError?: boolean;
};

const DEFAULT_LIMIT = 25;

export default function useFetchList<DataType, ExtendType = {}>(props: Props<IQuery<ExtendType>>) {
	const fetchable = props.fetchable === undefined ? true : props.fetchable;
	const apiRequest: AxiosInstance = props.apiRequest || httpRequest;
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [data, setData] = React.useState<Array<DataType>>([]);
	const [pagination, setPagination] = React.useState<IPagination>({
		...INITIAL_PAGINATION,
		perPage: props.limit || DEFAULT_LIMIT,
	} as IPagination);
	const [query, setQuery] = React.useState<IQuery<ExtendType>>({
		...INITIAL_QUERY,
		limit: props.limit || DEFAULT_LIMIT,
		...props.initialQuery,
	} as IQuery<ExtendType>);

	const searchParamName = props.searchParamName ?? "search";
	const [search, setSearch] = React.useState<string>();
	const [searchValue] = useDebounce(search, 500);

	const fetchList = async (abortController?: AbortController) => {
		if (!fetchable) {
			return;
		}
		try {
			setIsLoading(true);

			const res = await apiRequest.get<IHttpResponse<IPayloadPagination<DataType>>>(
				`${props.endpoint}${generateQueryString(query)}`,
				{
					signal: abortController?.signal,
				}
			);

			if (!res || !res.data) {
				return;
			}

			setPagination((oldVal) => {
				let limit = query?.limit || oldVal.perPage || props.limit || DEFAULT_LIMIT;
				let page =
					query?.offset !== undefined
						? Number(query.offset) === 0
							? 1
							: Number(query.offset) / limit + 1
						: oldVal?.page;
				return {
					...oldVal,
					page,
					perPage: limit,
					prev: res.data.payload.prev,
					next: res.data.payload.next,
					totalData: res.data.payload.count,
					countPage: Math.ceil(res.data.payload.count / limit),
				};
			});

			if (props.pushData) {
				setData((value) => [...value, ...res.data.payload.results]);
			} else {
				setData(res.data.payload.results);
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			// message.error(getErrorMessage(error));
			showErrorMessage(error, true, props.isRefreshWhenError);
		}
	};

	React.useEffect(() => {
		const abortController = new AbortController();
		fetchList(abortController);

		return () => {
			abortController.abort();
		};
		// eslint-disable-next-line
	}, [query, fetchable]);

	React.useEffect(() => {
		setQuery((e: IQuery<ExtendType>) => {
			return { ...e, [searchParamName]: searchValue as string, offset: 0 };
		});

		// eslint-disable-next-line
	}, [searchValue]);

	const changePage = (page: any, perPage: number) => {
		setPagination((oldVal) => {
			return {
				...oldVal,
				page,
				perPage,
			};
		});
		setQuery((oldVal) => {
			return {
				...oldVal,
				limit: perPage,
				offset: (page - 1) * (perPage || props.limit || DEFAULT_LIMIT),
			};
		});
	};

	const changeLimit = (perPage: number) => {
		setPagination((oldVal) => {
			return {
				...oldVal,
				perPage,
			};
		});
		setQuery((oldVal) => {
			return {
				...oldVal,
				limit: perPage,
				offset: 0,
			};
		});
	};

	return {
		DEFAULT_LIMIT,
		isLoading,
		data,
		pagination,
		query,
		setData,
		setPagination,
		setQuery,
		search,
		setSearch,
		changePage,
		fetchList,
		setIsLoading,
		changeLimit,
	};
}
